import type { BlockId } from '@unifyapps/defs/types/block';
import { useCallback } from 'react';
import { getPulseOwner } from '../../pulse/executionOwner';
import { pulse } from '../../pulse/floor';
import type { LogLevel, PulseLogInput } from '../../pulse/types';
import { usePulseScope } from '../usePulseScope';

const LOG_LEVELS: ReadonlySet<string> = new Set(['info', 'warn', 'error']);

export type ScriptLogFn = (message: unknown, payload?: unknown, level?: unknown) => void;

// matches pulse.error's message cap so a maker can't park a multi-MB string in the ring buffer
const LABEL_MAX_LENGTH = 500;

/**
 * Maker-facing Pulse logging with a positional console-like signature
 * `(message, payload?, level?)`, surfaced through the "log to debugger" action
 * (see useLogToDebugger). Returns a factory taking the action's `instanceId` so
 * a log fired from a repeatable row attributes to the ROW's scope (pm-014) —
 * handlers mount at the dispatcher, not inside the row tree, so the instance id
 * must come from the action args. Soft-validates inputs: maker input must never
 * throw on misuse.
 */
export function useScriptLog(): (instanceId?: string, blockId?: BlockId) => ScriptLogFn {
  const resolveScope = usePulseScope();

  return useCallback(
    (instanceId, blockId) => (message, payload, level) => {
      const label = (typeof message === 'string' ? message : String(message)).slice(
        0,
        LABEL_MAX_LENGTH,
      );
      const safeLevel: LogLevel =
        typeof level === 'string' && LOG_LEVELS.has(level) ? (level as LogLevel) : 'info';
      pulse.log(resolveScope(instanceId), {
        label,
        payload: payload as PulseLogInput['payload'],
        level: safeLevel,
        // block (explicit) wins; else the page function currently executing (getPulseOwner), so a log
        // fired inside a function body attributes to the function instead of a generic 'log'; else
        // undefined → the buffer's 'log' fallback. Matches useReportActionError's precedence.
        entity: blockId ? { type: 'block', id: blockId } : getPulseOwner(),
      });
    },
    [resolveScope],
  );
}
